/**
 * Whether agents may drive the keyboard and mouse on this computer.
 *
 * Windows only. macOS answers the same question through the OS Accessibility
 * grant, which the system asks for and the user revokes in System Settings —
 * there is nothing here to toggle, and offering one would give them two places
 * to look for the same answer.
 *
 * Rust owns the truth. Unlike every other setting in the app, this one cannot
 * live in `localStorage`: the tools it gates are reached over local HTTP by an
 * MCP server and never pass through the webview, so a value stored here would
 * be advisory at best. This module is a mirror, in the shape of
 * `use-login-item.ts` — read it from Rust, write it to Rust, never cache it.
 */

import { invoke } from "@tauri-apps/api/core";

import { isTauri } from "@/lib/is-tauri";
import { platformCapabilities } from "./platform-capabilities";

/** Whether this platform asks the app, rather than the OS, for consent. */
export function inputConsentApplies(): boolean {
  return isTauri() && platformCapabilities().requires_input_consent;
}

/** Whether agents are currently allowed to type and click. */
export async function getInputConsent(): Promise<boolean> {
  if (!inputConsentApplies()) return true;
  try {
    return await invoke<boolean>("desktop_input_consent");
  } catch {
    // Fail closed. A read that did not happen is not a grant, and showing the
    // toggle as on when the tools are refused would be worse than showing it
    // off when they work.
    return false;
  }
}

/** The recorded answer, or null when this prompt version was never answered. */
export async function getInputConsentDecision(): Promise<boolean | null> {
  if (!inputConsentApplies()) return true;
  try {
    return await invoke<boolean | null>("desktop_input_consent_decision");
  } catch {
    // A failed read is not proof of an answer. Treat it as unanswered so the
    // repair surface fails closed and gives the user a chance to decide.
    return null;
  }
}

/** Record the answer. Takes effect immediately, with no restart. */
export async function setInputConsent(granted: boolean): Promise<void> {
  if (!inputConsentApplies()) return;
  await invoke("set_desktop_input_consent", { granted });
}
